import { Chip, Drawer, IconButton, Box, Typography, CircularProgress } from "@wealthx/ui";
import React, { useEffect, useRef } from "react";
import { useQueryClient } from "react-query";

import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";

import { QUERY_KEY } from "../../commons/query-key";
import { useTenantStatus } from "../../hooks/tenant.hooks";
import { useBackofficeChat } from "../../hooks/useBackofficeChat";
import { useConversationResume } from "../../hooks/useConversationResume";
import { usePlanExecutor } from "../../hooks/usePlanExecutor";
import ChatInputBar from "../ChatInputBar";
import ChatMessageList from "../ChatMessageList";

import "./ai-chat-side-panel.scss";

const ChatIcon = () => (
  <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M27 10H23V6C23 5.46957 22.7893 4.96086 22.4142 4.58579C22.0391 4.21071 21.5304 4 21 4H5C4.46957 4 3.96086 4.21071 3.58579 4.58579C3.21071 4.96086 3 5.46957 3 6V22C3.00059 22.1881 3.05423 22.3723 3.15478 22.5313C3.25532 22.6903 3.39868 22.8177 3.56839 22.8989C3.7381 22.9801 3.92728 23.0118 4.11418 22.9903C4.30108 22.9689 4.47814 22.8951 4.625 22.7775L9 19.25V23C9 23.5304 9.21071 24.0391 9.58579 24.4142C9.96086 24.7893 10.4696 25 11 25H22.6987L27.375 28.7775C27.5519 28.9206 27.7724 28.9991 28 29C28.2652 29 28.5196 28.8946 28.7071 28.7071C28.8946 28.5196 29 28.2652 29 28V12C29 11.4696 28.7893 10.9609 28.4142 10.5858C28.0391 10.2107 27.5304 10 27 10ZM8.31875 17.2225L5 19.9062V6H21V17H8.9475C8.71863 17 8.4967 17.0786 8.31875 17.2225ZM27 25.9062L23.6812 23.2225C23.5043 23.0794 23.2838 23.0009 23.0562 23H11V19H21C21.5304 19 22.0391 18.7893 22.4142 18.4142C22.7893 18.0391 23 17.5304 23 17V12H27V25.9062Z"
      fill="black"
    />
  </svg>
);

interface AIChatSidePanelProps {
  isOpen: boolean;
  onClose: () => void;
}

const AIChatSidePanel: React.FC<AIChatSidePanelProps> = ({ isOpen, onClose }) => {
  const { hasTenant, tenantID, agencyID, isLoading } = useTenantStatus();
  const queryClient = useQueryClient();

  const {
    messages,
    setMessages,
    isStreaming,
    currentStep,
    inputValue,
    files,
    conversationID,
    setInputValue,
    setFiles,
    handleSendMessage,
    resetChat,
    loadConversation,
  } = useBackofficeChat(tenantID, isOpen, agencyID);

  const { actionStatuses, executingPlans, handlePlanClick, resetPlanExecutor } = usePlanExecutor({
    messages,
    setMessages,
    tenantId: tenantID,
    agencyId: agencyID,
    conversationID,
  });

  const { reset: resetResume } = useConversationResume({
    agencyId: agencyID,
    loadConversation,
    resetPlanExecutor,
    resetChat,
  });

  // Invalidate conversation list when new messages are added
  const prevMessagesLenRef = useRef(messages.length);
  useEffect(() => {
    if (messages.length > prevMessagesLenRef.current && conversationID) {
      queryClient.invalidateQueries([QUERY_KEY.CONVERSATION_LIST, agencyID]);
    }
    prevMessagesLenRef.current = messages.length;
  }, [messages.length, conversationID, agencyID, queryClient]);

  const handleReload = () => {
    resetChat();
    resetPlanExecutor();
    resetResume();
  };

  const showOnboarding = !isLoading && !hasTenant;

  const renderPanelContent = () => {
    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <CircularProgress size={30} />
        </Box>
      );
    }
    if (showOnboarding) {
      return (
        <div className="ai-chat-side-panel__onboarding">
          <Typography variant="body1" className="ai-chat-side-panel__onboarding-text">
            Workspace not initialized. Please set up your AI Agent workspace first.
          </Typography>
        </div>
      );
    }
    return (
      <>
        {messages.length === 0 && (
          <div className="ai-chat-side-panel__hero">
            <Box className="ai-chat-side-panel__hero-card">
              <Chip
                label="AI-Chat For Brokers"
                className="ai-chat-side-panel__hero-badge"
                size="small"
                variant="outlined"
                icon={<span className="ai-chat-side-panel__hero-badge-dot" />}
              />
              <Typography className="ai-chat-side-panel__hero-description">
                A safe and secure way to chat about insights that run your business and utilise AI.
                All chats stay within your environment, all closed off and compliant.
              </Typography>
              <Box className="ai-chat-side-panel__hero-features">
                <Box className="ai-chat-side-panel__hero-feature">
                  <Typography className="ai-chat-side-panel__hero-feature-text">
                    🤖 Smart
                  </Typography>
                </Box>
                <Box className="ai-chat-side-panel__hero-feature">
                  <Typography className="ai-chat-side-panel__hero-feature-text">⚡ Fast</Typography>
                </Box>
                <Box className="ai-chat-side-panel__hero-feature">
                  <Typography className="ai-chat-side-panel__hero-feature-text">
                    🔒 Secure
                  </Typography>
                </Box>
              </Box>
            </Box>
          </div>
        )}

        {messages.length > 0 && (
          <Box className="ai-chat-side-panel__messages-container">
            <ChatMessageList
              messages={messages}
              currentStep={currentStep}
              onPlanClick={handlePlanClick}
              actionStatuses={actionStatuses}
              executingPlans={executingPlans}
            />
          </Box>
        )}
      </>
    );
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      variant="temporary"
      className="ai-chat-side-panel-drawer"
      PaperProps={{
        sx: { width: "385px" },
      }}
    >
      <div className="ai-chat-side-panel">
        <div className="ai-chat-side-panel__header">
          <div className="ai-chat-side-panel__header-title">
            <ChatIcon />
            <Typography variant="h6" className="ai-chat-side-panel__title-text">
              Wealthx AI
            </Typography>
          </div>
          <div className="ai-chat-side-panel__header-actions">
            <IconButton
              size="small"
              onClick={handleReload}
              className="ai-chat-side-panel__action-btn"
              title="Reload conversation"
            >
              <RefreshIcon />
            </IconButton>
            <IconButton
              size="small"
              onClick={onClose}
              className="ai-chat-side-panel__action-btn"
              title="Close"
            >
              <CloseIcon />
            </IconButton>
          </div>
        </div>

        <div className="ai-chat-side-panel__content">{renderPanelContent()}</div>

        {!showOnboarding && (
          <div className="ai-chat-side-panel__input-wrapper">
            <ChatInputBar
              value={inputValue}
              onChange={setInputValue}
              onSend={handleSendMessage}
              disabled={isStreaming || isLoading || !tenantID}
              files={files}
              onFilesChange={setFiles}
            />
          </div>
        )}
      </div>
    </Drawer>
  );
};

export default AIChatSidePanel;
